import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PieChartOutlineIcon from "@mui/icons-material/PieChartOutline";

import BackgroundWrapper from "../BackgroundWrapper";
import { useAppSelector } from "../../store";
import { getTournoi } from "../../features/tournoi/tournoiSlice";
import type { PublicInscritRow, PublicInscritsListResponse } from "../../types";
import { fetchInscritsListClient } from "../../helpers/fetchInscritsListClient";

/** Ajoute « pts » seulement si la valeur est un nombre (virgule / point décimal, espaces autorisés). */
function formatPointsChipLabel(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const compact = trimmed.replace(/\s/g, "");
  if (/pts\.?$/i.test(compact)) return trimmed;
  if (/^-?\d+([.,]\d+)?$/.test(compact)) {
    return `${trimmed} pts`;
  }
  return trimmed;
}

const InscritsPage: React.FC = () => {
  const theme = useTheme();
  const tournoi = useAppSelector(getTournoi);
  const tournamentId =
    typeof tournoi?.id === "string"
      ? tournoi.id
      : tournoi?.id != null
        ? String(tournoi.id)
        : undefined;
  const tournamentLabel = tournoi?.label?.trim() || "Tournoi";

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<PublicInscritsListResponse | null>(
    null
  );

  const load = useCallback(async () => {
    if (!tournamentId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchInscritsListClient(tournamentId);
      setPayload(data);
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "message" in e
          ? String((e as { message?: string }).message)
          : "Impossible de charger la liste.";
      setError(msg);
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => payload?.rows ?? [], [payload?.rows]);
  const totalPlaces = payload?.totalPlaces ?? 0;
  const totalInscrits = payload?.totalInscrits ?? 0;
  const fillRate =
    totalPlaces > 0 ? Math.round((totalInscrits / totalPlaces) * 1000) / 10 : 0;

  const grouped = useMemo(() => {
    const map = new Map<
      string,
      { dateLabel: string | null; rows: PublicInscritRow[] }
    >();
    for (const r of rows) {
      const cur = map.get(r.boardTitle);
      if (!cur) {
        map.set(r.boardTitle, {
          dateLabel: r.boardDateLabel,
          rows: [r],
        });
      } else {
        cur.rows.push(r);
      }
    }
    return Array.from(map.entries()).sort(([a], [b]) =>
      a.localeCompare(b, "fr")
    );
  }, [rows]);

  const isDark = theme.palette.mode === "dark";

  const statCardSx = {
    borderRadius: 3,
    border: "1px solid",
    borderColor: "divider",
    background: isDark
      ? alpha(theme.palette.primary.main, 0.12)
      : alpha(theme.palette.primary.main, 0.06),
    height: "100%",
    boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.35)" : 2,
  } as const;

  const sectionOverlineSx = {
    fontWeight: 800,
    letterSpacing: "0.14em",
    color: isDark ? "secondary.light" : "primary.main",
    display: "block",
    mb: 1,
  } as const;

  return (
    <BackgroundWrapper>
      <Container
        maxWidth="lg"
        sx={{
          pt: { xs: 0.5, sm: 0.75 },
          pb: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography
              variant="overline"
              sx={{
                fontWeight: 800,
                letterSpacing: "0.14em",
                color: isDark ? "secondary.light" : "primary.main",
              }}
            >
              Tournoi
            </Typography>
            <Typography variant="h4" component="h1" fontWeight={800} sx={{ mt: 0.5 }}>
              Inscrits — {tournamentLabel}
            </Typography>
          </Box>

          {loading && <LinearProgress aria-label="Chargement de la liste" />}

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {!tournamentId && (
            <Alert severity="info">
              Aucun tournoi courant n’est chargé. Revenez depuis l’accueil ou contactez
              l’organisation.
            </Alert>
          )}

          {payload && (
            <Box
              component="section"
              aria-labelledby="inscrits-stats-heading"
              sx={{ width: "100%" }}
            >
              <Typography
                id="inscrits-stats-heading"
                component="h2"
                variant="overline"
                sx={sectionOverlineSx}
              >
                Vue d’ensemble
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                  gap: 2,
                  width: "100%",
                }}
              >
                <Card elevation={0} sx={statCardSx}>
                  <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <PeopleOutlineIcon color="secondary" sx={{ fontSize: 36 }} />
                      <Box>
                        <Typography variant="h5" fontWeight={800}>
                          {totalInscrits}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Inscriptions
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
                <Card elevation={0} sx={statCardSx}>
                  <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <EventAvailableIcon color="secondary" sx={{ fontSize: 36 }} />
                      <Box>
                        <Typography variant="h5" fontWeight={800}>
                          {totalPlaces}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Places disponibles
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
                <Card elevation={0} sx={statCardSx}>
                  <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <PieChartOutlineIcon color="secondary" sx={{ fontSize: 36 }} />
                      <Box>
                        <Typography variant="h5" fontWeight={800}>
                          {fillRate}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Taux de remplissage global
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}

          {payload && (
            <Box
              component="section"
              aria-labelledby="inscrits-boards-heading"
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Divider
                role="presentation"
                sx={{
                  borderColor: "divider",
                  borderBottomWidth: 2,
                }}
              />
              <Typography
                id="inscrits-boards-heading"
                component="h2"
                variant="overline"
                sx={{ ...sectionOverlineSx, mb: 0 }}
              >
                Inscriptions par tableau
              </Typography>
              {!loading && rows.length === 0 && (
                <Alert severity="info">
                  Aucune inscription active pour le moment (ou inscriptions en cours de synchronisation).
                </Alert>
              )}
              {grouped.map(([boardTitle, { dateLabel, rows: boardRows }]) => (
                <Card
                  key={boardTitle}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    overflow: "visible",
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.35)" : 2,
                  }}
                >
                  <Box
                    sx={{
                      position: "sticky",
                      zIndex: 2,
                      top: { xs: 56, sm: 68 },
                      px: 2,
                      py: 1.25,
                      bgcolor: isDark ? "grey.900" : "background.paper",
                      backgroundImage: isDark
                        ? `linear-gradient(120deg, ${alpha(theme.palette.primary.main, 0.22)} 0%, ${theme.palette.grey[900]} 42%)`
                        : `linear-gradient(120deg, ${alpha(theme.palette.primary.light, 0.3)} 0%, ${theme.palette.background.paper} 42%)`,
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography variant="subtitle1" fontWeight={800}>
                          {boardTitle}
                        </Typography>
                        {dateLabel && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 0.25 }}
                          >
                            {dateLabel}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        size="small"
                        label={`${boardRows.length} inscrit${boardRows.length > 1 ? "s" : ""}`}
                        sx={{ fontWeight: 700, flexShrink: 0 }}
                        color="secondary"
                        variant={isDark ? "filled" : "outlined"}
                      />
                    </Stack>
                  </Box>
                  <Divider />

                  <Box sx={{ display: { xs: "block", sm: "none" } }}>
                    <Stack divider={<Divider flexItem />} sx={{ px: 0 }}>
                      {boardRows.map((row, idx) => (
                        <Stack
                          key={`${row.playerLine}-${row.registeredAt ?? ""}-${idx}`}
                          direction="column"
                          alignItems="stretch"
                          spacing={0.5}
                          sx={{
                            px: 2,
                            py: 0.65,
                            "&:hover": {
                              bgcolor: isDark ? "action.hover" : "grey.50",
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{ overflowWrap: "break-word" }}
                          >
                            {row.playerLine}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            flexWrap="wrap"
                            useFlexGap
                            justifyContent="flex-start"
                          >
                            {row.points ? (
                              <Chip
                                size="small"
                                label={formatPointsChipLabel(row.points)}
                                color="secondary"
                                variant="outlined"
                                sx={{ fontWeight: 600 }}
                              />
                            ) : null}
                            {row.club ? (
                              <Chip size="small" label={row.club} variant="outlined" />
                            ) : null}
                            {row.categorie ? (
                              <Chip size="small" label={row.categorie} variant="outlined" />
                            ) : null}
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      display: { xs: "none", sm: "block" },
                      borderBottomLeftRadius: 12,
                      borderBottomRightRadius: 12,
                      overflow: "hidden",
                    }}
                  >
                    <TableContainer component={Box}>
                      <Table
                        size="small"
                        padding="none"
                        aria-label={`Inscrits — ${boardTitle}`}
                        sx={{ tableLayout: "fixed", width: "100%" }}
                      >
                        <TableHead>
                          <TableRow
                            sx={{
                              bgcolor: isDark
                                ? alpha(theme.palette.common.black, 0.35)
                                : alpha(theme.palette.primary.main, 0.06),
                            }}
                          >
                            <TableCell
                              component="th"
                              scope="col"
                              sx={{ fontWeight: 700, py: 0.85, px: 2, width: "44%" }}
                            >
                              Joueur
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="col"
                              align="right"
                              sx={{
                                fontWeight: 700,
                                py: 0.85,
                                px: 1.5,
                                width: "11%",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Points
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="col"
                              sx={{ fontWeight: 700, py: 0.85, px: 1.5, width: "33%" }}
                            >
                              Club
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="col"
                              sx={{
                                fontWeight: 700,
                                py: 0.85,
                                px: 2,
                                width: "12%",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Cat.
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {boardRows.map((row, idx) => (
                            <TableRow
                              key={`${row.playerLine}-${row.registeredAt ?? ""}-${idx}`}
                              hover
                              sx={{
                                "&:nth-of-type(even)": {
                                  bgcolor: isDark
                                    ? alpha(theme.palette.common.white, 0.04)
                                    : alpha(theme.palette.common.black, 0.025),
                                },
                              }}
                            >
                              <TableCell
                                sx={{
                                  py: 0.55,
                                  px: 2,
                                  verticalAlign: "middle",
                                  borderColor: "divider",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  sx={{ overflowWrap: "break-word" }}
                                >
                                  {row.playerLine}
                                </Typography>
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  py: 0.55,
                                  px: 1.5,
                                  verticalAlign: "middle",
                                  whiteSpace: "nowrap",
                                  borderColor: "divider",
                                }}
                              >
                                {row.points ? (
                                  <Typography
                                    variant="body2"
                                    fontWeight={700}
                                    color="secondary.main"
                                    sx={{ fontVariantNumeric: "tabular-nums" }}
                                  >
                                    {formatPointsChipLabel(row.points)}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.disabled">
                                    —
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell
                                sx={{
                                  py: 0.55,
                                  px: 1.5,
                                  verticalAlign: "middle",
                                  maxWidth: 0,
                                  borderColor: "divider",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  noWrap
                                  title={row.club || undefined}
                                >
                                  {row.club || "—"}
                                </Typography>
                              </TableCell>
                              <TableCell
                                sx={{
                                  py: 0.55,
                                  px: 2,
                                  verticalAlign: "middle",
                                  whiteSpace: "nowrap",
                                  borderColor: "divider",
                                }}
                              >
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  {row.categorie || "—"}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Card>
              ))}
            </Box>
          )}
        </Stack>
      </Container>
    </BackgroundWrapper>
  );
};

export default InscritsPage;
